
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const MarketAnalysis = () => {
  const [input, setInput] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);

  // Backend API URL configuration
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Poll for job status
  useEffect(() => {
    let pollInterval;
    let startTime;
    
    if (jobId && loading) {
      startTime = Date.now();
      const TIMEOUT_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
      
      pollInterval = setInterval(async () => {
        try {
          // Check if 5 minutes have passed
          if (Date.now() - startTime > TIMEOUT_DURATION) {
            clearInterval(pollInterval);
            setError('Job polling timed out after 5 minutes');
            setLoading(false);
            setJobId(null);
            console.log(`Market analysis job ${jobId} polling timed out after 5 minutes`);
            return;
          }

          // Using environment variable for API URL
          const response = await axios.get(`${API_URL}/api/job-status/${jobId}`);
          const status = response.data;
          
          setJobStatus(status);
          
          if (status.status === 'completed') {
            setResult(status.result);
            setLoading(false);
            setJobId(null);
          } else if (status.status === 'error') {
            setError(status.error || 'Processing failed');
            setLoading(false);
            setJobId(null);
          }
        } catch (err) {
          console.error('Error checking job status:', err);
          setError('Failed to check job status');
          setLoading(false);
          setJobId(null);
        }
      }, 3000); // Poll every 3 seconds
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [jobId, loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    setLoading(true);
    setError(null);
    setResult(null);
    setJobId(null);
    setJobStatus(null);

    try {
      // Using environment variable for API URL
      const response = await axios.post(`${API_URL}/api/market-analysis`, {
        input: input,
        timestamp: new Date().toISOString(),
      }, {
      });
      
      // Response should contain jobId
      setJobId(response.data.jobId);
      setJobStatus(response.data);
      
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start analysis');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen ">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in">
        {/* Header */}
        <div className="text-center mb-13">
          <h1 className="text-5xl font-bold bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 bg-clip-text text-transparent mb-6 py-2 leading-normal">
             Elite Market Intelligence
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed py-2 leading-normal">
            Uncover market dynamics, competitive landscapes, and strategic opportunities with precision analytics
          </p>
        </div>

        {/* Main Form Card */}
        <div className="max-w-4xl mx-auto bg-gradient-to-br from-gray-900 to-black border border-yellow-500/20 rounded-3xl shadow-2xl p-8 mb-12 mt-12 backdrop-blur-sm">
          <form onSubmit={handleSubmit} className="space-y-8">
            <div>
              <label htmlFor="market" className="block text-lg font-semibold text-yellow-400 mb-4">
                Which market sector requires deep analysis?
              </label>
              <input
                id="market"
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="describe your product or copy the target customer and who to reach out from Business Insight"
                className="w-full px-6 py-4 bg-black/50 border-2 border-yellow-500/30 rounded-2xl text-white placeholder-gray-400 focus:ring-4 focus:ring-yellow-500/20 focus:border-yellow-500 transition-all duration-300 text-lg backdrop-blur-sm"
                required
              />
            </div>
            
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className={`group relative w-full py-5 px-8 rounded-2xl font-bold text-lg transition-all duration-300 transform focus:outline-none overflow-hidden ${
                loading || !input.trim()
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-500 text-black hover:from-yellow-400 hover:via-yellow-500 hover:to-yellow-400 hover:scale-[1.02] hover:shadow-2xl hover:shadow-yellow-500/30 active:scale-[0.98]'
              }`}
            >
              {!loading && !input.trim() && (
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/0 via-yellow-400/10 to-yellow-400/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-pulse"></div>
              )}
              {loading || !input.trim() || (
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-opacity duration-300"></div>
              )}
              <span className="relative z-10">
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-6 h-6 border-3 border-yellow-400 border-t-transparent rounded-full animate-spin mr-3"></div>
                    {jobStatus?.status === 'pending' ? 'Deep Market Analysis in Progress... (3-4 minutes)' : 'Initializing Market Intelligence...'}
                  </div>
                ) : (
                  'Generate Market Intelligence Report'
                )}
              </span>
            </button>
          </form>
        </div>

        {/* Status Message */}
        {loading && jobStatus && (
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-blue-900/20 to-blue-800/20 border border-blue-500/30 p-6 mb-8 rounded-2xl animate-slide-up backdrop-blur-sm">
            <div className="flex items-center">
              <div className="text-blue-400 mr-4 text-2xl">⏳</div>
              <div>
                <h3 className="text-blue-300 font-semibold text-lg">Market Intelligence Processing</h3>
                <p className="text-blue-200">{jobStatus.message}</p>
                <p className="text-blue-300 text-sm mt-2 font-mono">Analysis ID: {jobId}</p>
                <p className="text-blue-300 text-sm">Estimated completion: {jobStatus.estimatedTime}</p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-red-900/20 to-red-800/20 border border-red-500/30 p-6 mb-8 rounded-2xl animate-slide-up backdrop-blur-sm">
            <div className="flex items-center">
              <div className="text-red-400 mr-4 text-2xl">⚠️</div>
              <div>
                <h3 className="text-red-300 font-semibold text-lg">Analysis Error</h3>
                <p className="text-red-200">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {result && (
          <div className="max-w-7xl mx-auto bg-gradient-to-br from-gray-900 to-black border border-yellow-500/20 rounded-3xl shadow-2xl p-8 animate-slide-up backdrop-blur-sm">
            <h2 className="text-3xl font-bold text-yellow-400 mb-8 flex items-center">
              Comprehensive Market Intelligence Report
            </h2>
            <div className="bg-black/40 rounded-2xl p-8 border border-yellow-500/10">
              {typeof result === 'string' ? (
                <div className="prose prose-lg prose-invert max-w-none text-gray-200">
                  <ReactMarkdown>
                    {result.startsWith('```markdown\n') && result.endsWith('\n```') 
                      ? result.slice(11, -4) // Extract markdown from code block
                      : result
                    }
                  </ReactMarkdown>
                </div>
              ) : result?.result ? (
                <div className="prose prose-lg prose-invert max-w-none text-gray-200">
                  <ReactMarkdown>
                    {typeof result.result === 'string' ? result.result : JSON.stringify(result.result, null, 2)}
                  </ReactMarkdown>
                </div>
              ) : (
                <pre className="text-gray-200 whitespace-pre-wrap text-lg leading-relaxed font-mono">
                  {JSON.stringify(result, null, 2)}
                </pre>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketAnalysis;